import React, { useState, useEffect } from "react";
import { useTable } from "react-table";

// bootstrap
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

// custom components
import { Card, SearchBar } from "_atoms";
// styles
import { Colors, Spacing } from "_styles";

// tremor
import { Title as TremorTitle, Subtitle, Flex } from "@tremor/react";

//icons
import { FaCirclePlus } from "react-icons/fa6";
import { MdFirstPage, MdChevronLeft, MdChevronRight, MdLastPage } from "react-icons/md";
import { BsDownload } from "react-icons/bs";

// functions
import { manualPaginate } from "_utils/functions";

// constants
const locationsPerPage = 20;

const AirportLocationsTable = ({
    columns,
    data,
    title,
    description,
    handleOpenModal,
    locationSearchValue,
    handleSearchLocation,
    handleExportCSV
}) => {
    //#region local states
    const [currentPage, setCurrentPage] = useState(1);
    const [paginatedData, setPaginatedData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    //#endregion

    //#region useEffect
    useEffect(() => {
        if (Array.isFullArray(data)) {
            // calculate total pages
            const total = Math.ceil(data.length / locationsPerPage);
            setTotalPages(total);

            // paginate data
            const paginated = manualPaginate(
                data,
                locationsPerPage,
                currentPage
            );
            setPaginatedData(paginated.requiredArr);
        } else {
            setTotalPages(0);
            setPaginatedData([]);
        }
    }, [data, currentPage]);
    //#endregion

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({ columns, data: paginatedData });

    //#region functions
    // function to go to the first page
    const firstPage = () => {
        setCurrentPage(1);
    };

    // function to go to the previous page
    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // function to go to the next page
    const nextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    // function to go to the last page
    const lastPage = () => {
        setCurrentPage(totalPages);
    };
    //#endregion

    const iconStyles = {
        marginInlineEnd: "20px",
        fontSize: "26px",
        borderRadius: "4px",
        padding: "2px 6px",
        transition: "all 0.3s ease", // Add a smooth transition
    };

    return (
        <Card >
            {/* heading */}
            <Row>
                <Col>
                    <Flex>
                        {/* title */}
                        <div>
                            <TremorTitle>{title}</TremorTitle>
                            <Subtitle>{description}</Subtitle>
                        </div>
                        <div className="d-flex gap-2 align-items-center">
                            {/* Search Location */}
                            <SearchBar onChange={(val) => handleSearchLocation(val.target.value)} placeholder={"Search Location"} value={locationSearchValue} isTimeline={false} />
                            {/* export to CSV */}
                            <BsDownload
                                onClick={handleExportCSV}
                                style={{ cursor: "pointer" }}
                                color={"lightgrey"}
                                size={22}
                                title="Export to CSV"
                            />
                            {/* add drop off location icon */}
                            <FaCirclePlus
                                onClick={handleOpenModal}
                                style={{ marginLeft: Spacing.SCALE_8, cursor: "pointer" }}
                                color={"lightgrey"}
                                size={28}
                            />
                        </div>
                    </Flex>
                </Col>
            </Row>

            {/* table contents */}
            {Array.isFullArray(data) ? (
                <>
                    <div style={{ maxHeight: "517px", overflowX: "scroll" }}>
                        <table {...getTableProps()} style={{ width: "100%" }}>
                            {/* column headers */}
                            <thead>
                                {headerGroups.map((headerGroup) => (
                                    <tr {...headerGroup.getHeaderGroupProps()}>
                                        {headerGroup.headers.map((column) => (
                                            <th style={{ borderBottomWidth: "1px", borderBottomColor: "#F2F2F2", borderBottomStyle: "solid", width: "25%" }} {...column.getHeaderProps()}>
                                                <div style={{ marginTop: 14 }}>
                                                    {column.render("Header")}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>

                            {/* body */}
                            <tbody {...getTableBodyProps()}>
                                {rows.map((row, i) => {
                                    prepareRow(row);
                                    return (
                                        <>
                                            <tr {...row.getRowProps()} style={{ borderBottomWidth: "1px", borderTopWidth: "1px", borderBottomColor: "#F2F2F2", borderTopColor: "#F2F2F2", borderBottomStyle: "solid", height: 50, fontSize: 12, cursor: "auto" }}>
                                                {row.cells.map((cell) => {
                                                    return (
                                                        <td style={{ padding: 12 }} {...cell.getCellProps()}>
                                                            <div style={{ width: "80%" }}>
                                                                {cell.render("Cell")}
                                                            </div>

                                                        </td>
                                                    );
                                                })}
                                            </tr>

                                        </>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #F2F2F2' }}>
                            <MdFirstPage
                                onClick={firstPage}
                                style={{
                                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                    opacity: currentPage === 1 ? 0.5 : 1,
                                    fontSize: '24px',
                                    color: currentPage === 1 ? '#ccc' : Colors.PRM_LIGHT_BLUE
                                }}
                                disabled={currentPage === 1}
                            />
                            <MdChevronLeft
                                onClick={prevPage}
                                style={{
                                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                    opacity: currentPage === 1 ? 0.5 : 1,
                                    fontSize: '24px',
                                    color: currentPage === 1 ? '#ccc' : Colors.PRM_LIGHT_BLUE
                                }}
                                disabled={currentPage === 1}
                            />
                            <span style={{ margin: '0 1rem', alignSelf: 'center' }}>
                                {(() => {
                                    const totalItems = Array.isFullArray(data) ? data.length : 0;
                                    const start = totalItems > 0 ? (currentPage - 1) * locationsPerPage + 1 : 0;
                                    const end = totalItems > 0 ? Math.min(currentPage * locationsPerPage, totalItems) : 0;
                                    return totalItems > 0 ? `${start}-${end} of ${totalItems}` : '0-0 of 0';
                                })()}
                            </span>
                            <MdChevronRight
                                onClick={nextPage}
                                style={{
                                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                    opacity: currentPage === totalPages ? 0.5 : 1,
                                    fontSize: '24px',
                                    color: currentPage === totalPages ? '#ccc' : Colors.PRM_LIGHT_BLUE
                                }}
                                disabled={currentPage === totalPages}
                            />
                            <MdLastPage
                                onClick={lastPage}
                                style={{
                                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                    opacity: currentPage === totalPages ? 0.5 : 1,
                                    fontSize: '24px',
                                    color: currentPage === totalPages ? '#ccc' : Colors.PRM_LIGHT_BLUE
                                }}
                                disabled={currentPage === totalPages}
                            />
                        </div>
                    )}
                </>
            ) : (
                <Row style={{ opacity: 0.9 }} className="d-flex justify-content-center">
                    <p className="mt-3" style={{ textAlign: "center", color: Colors.PRM_LIGHT_BLUE }}>
                        {"No Airport Locations Found ."}
                    </p>
                </Row>
            )}
        </Card>
    );
};

export default AirportLocationsTable;
